import axios from 'axios';
import { toast } from 'sonner';
import { TrendingDown, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DIV_CHANGE_EVENT = 'div-change-alerts-changed';

// One "don't show again" for a single cut/raise — keyed by change date server-side,
// so the next change on the same ticker surfaces again.
export async function dismissDivChangeAlert(alert) {
  await axios.post(`${API}/journal/dividend-cut-alerts/dismiss`,
    { ticker: alert.ticker, last_date: alert.last_date || '' }, { withCredentials: true });
  window.dispatchEvent(new Event(DIV_CHANGE_EVENT));
}

export function divChangePrefill(a) {
  const pct = a.change_pct != null ? `${a.change_pct > 0 ? '+' : ''}${Number(a.change_pct).toFixed(1)}%` : '';
  const isCut = a.kind !== 'raise';
  return {
    entry_type: isCut ? 'dividend_cut' : 'free',
    ticker: a.ticker,
    title: `${a.ticker} dividend ${isCut ? 'cut' : 'raise'}${pct ? ` (${pct})` : ''}`,
    body: '',
    context_snapshot: {
      div_change_kind: isCut ? 'cut' : 'raise',
      cut_pct: a.change_pct,
      prev_amount: a.prev_amount,
      new_amount: a.last_amount,
      cut_date: a.last_date,
    },
  };
}

export function showDivChangeToast(a, { onJournal }) {
  const isCut = a.kind !== 'raise';
  const pct = a.change_pct != null ? `${Math.abs(Number(a.change_pct)).toFixed(1)}%` : '';
  const Icon = isCut ? TrendingDown : TrendingUp;
  return toast.custom((id) => (
    <div
      role="status"
      data-testid={`div-change-toast-${a.ticker}`}
      className={`relative rounded-lg border bg-background text-foreground shadow-lg p-3 pr-3 min-w-[340px] max-w-md ${isCut ? 'border-rose-400/50' : 'border-emerald-400/50'}`}
    >
      <button
        type="button"
        onClick={async () => { toast.dismiss(id); try { await dismissDivChangeAlert(a); } catch { /* silent */ } }}
        className="absolute top-1.5 right-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
        data-testid={`div-change-toast-dont-show-${a.ticker}`}
      >
        Don't show again
      </button>
      <div className="flex items-center gap-1.5 text-sm font-semibold pr-28">
        <Icon className={`w-4 h-4 ${isCut ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`} />
        Dividend {isCut ? 'cut' : 'raise'} on {a.ticker}
      </div>
      <p className="text-xs text-foreground/75 mt-1 leading-snug">
        {a.name} {isCut ? 'cut' : 'raised'} its dividend{pct ? ` by ${pct}` : ''}. Journal your reaction so future-you remembers what you decided.
      </p>
      <div className="flex items-center justify-end gap-2 mt-2">
        <button
          type="button"
          onClick={() => toast.dismiss(id)}
          className="rounded-md border border-border px-2.5 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          data-testid={`div-change-toast-later-${a.ticker}`}
        >
          Maybe later
        </button>
        <button
          type="button"
          onClick={() => { toast.dismiss(id); onJournal(divChangePrefill(a)); }}
          className="rounded-md bg-foreground text-background px-2.5 py-1 text-xs font-semibold hover:opacity-90 transition-opacity"
          data-testid={`div-change-toast-journal-${a.ticker}`}
        >
          Journal it
        </button>
      </div>
    </div>
  ), { duration: 14000 });
}
